import { useEffect, useState } from "react";
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Group,
  TextInput,
  Textarea,
  Button,
  NumberInput,
  Divider,
  FileInput,
  ActionIcon,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useNavigate } from "react-router-dom";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useCreateProjectSummarySheet } from "../hooks/useProjectSummarySheet";
import type { CreateProjectSummarySheetDto } from "../../../api/projectSummarySheet";
import { projectSummarySheetApi } from "../../../api/projectSummarySheet";
import { thaanaFieldInputStyles } from "../../../utils/dhivehiText";
import { autoResizeTextareaStyles, resizeTextareaToContent } from "../../../utils/textareaAutoResize";
import { useThaanaFormSync } from "../../../hooks/useThaanaFormSync";
import { RegulatoryClausePicker } from "../../../components/RegulatoryClausePicker";

type BudgetRow = { budgetYear: string; allocatedAmount: string };

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result as string;
      const b64 = r.includes(",") ? r.split(",")[1] : r;
      resolve(b64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function ProjectSummarySheetForm() {
  const navigate = useNavigate();
  const create = useCreateProjectSummarySheet();
  const [file, setFile] = useState<File | null>(null);

  const form = useForm({
    initialValues: {
      summarySheetNumber: "",
      sheetDate: new Date() as Date | null,
      projectNumber: "",
      projectName: "",
      fundingAgency: "",
      scopeOfWorkDetail: "",
      estimatedCost: "" as string | number,
      numberOfTimesAnnounced: "" as string | number,
      dateAnnounced: null as Date | null,
      numberOfEoi: "" as string | number,
      numberOfBidsSubmitted: "" as string | number,
      submittedParty: "",
      awardedParty: "",
      proposedPrice: "" as string | number,
      proposedDuration: "",
      detailsOfTheProject: "",
      bidCommitteeReference: "",
      agendaReference: "",
      budgetAllocations: [] as BudgetRow[],
    },
    validate: {
      summarySheetNumber: (v) => (!v ? "Required" : null),
      sheetDate: (v) => (!v ? "Required" : null),
      projectName: (v) => (!v ? "Required" : null),
    },
  });

  useThaanaFormSync(form, { autoResizeSelector: ".pss-auto-resize-textarea" });

  useEffect(() => {
    const year = form.values.sheetDate?.getFullYear() ?? new Date().getFullYear();
    projectSummarySheetApi
      .getNextSheetNumber(year)
      .then(({ summarySheetNumber }) => {
        form.setFieldValue("summarySheetNumber", summarySheetNumber);
      })
      .catch(() => {
        // Non-blocking; backend will assign on create
      });
  }, [form.values.sheetDate]);

  useEffect(() => {
    const resize = () => {
      document
        .querySelectorAll(".pss-auto-resize-textarea textarea")
        .forEach((el) => resizeTextareaToContent(el as HTMLTextAreaElement));
    };
    const t = window.setTimeout(resize, 0);
    return () => window.clearTimeout(t);
  }, [form.values.scopeOfWorkDetail, form.values.detailsOfTheProject]);

  const addBudgetRow = () => {
    form.insertListItem("budgetAllocations", { budgetYear: "", allocatedAmount: "" });
  };

  const handleSubmit = async (values: typeof form.values) => {
    const budgetAllocations: CreateProjectSummarySheetDto["budgetAllocations"] = [];
    let i = 0;
    for (const row of values.budgetAllocations) {
      const y = parseInt(String(row.budgetYear), 10);
      const amt = parseFloat(String(row.allocatedAmount));
      if (!Number.isNaN(y) && !Number.isNaN(amt)) {
        budgetAllocations.push({ budgetYear: y, allocatedAmount: amt, sortOrder: i++ });
      }
    }

    let base64File: string | undefined;
    let fileName: string | undefined;
    if (file) {
      base64File = await readFileAsBase64(file);
      fileName = file.name;
    }

    const payload: CreateProjectSummarySheetDto = {
      summarySheetNumber: values.summarySheetNumber,
      sheetDate: (values.sheetDate || new Date()).toISOString(),
      projectNumber: values.projectNumber || null,
      projectName: values.projectName,
      fundingAgency: values.fundingAgency || null,
      scopeOfWorkDetail: values.scopeOfWorkDetail || null,
      estimatedCost:
        values.estimatedCost === "" || values.estimatedCost === undefined
          ? null
          : Number(values.estimatedCost),
      numberOfTimesAnnounced:
        values.numberOfTimesAnnounced === "" || values.numberOfTimesAnnounced === undefined
          ? null
          : Number(values.numberOfTimesAnnounced),
      dateAnnounced: values.dateAnnounced ? values.dateAnnounced.toISOString() : null,
      numberOfEoi:
        values.numberOfEoi === "" || values.numberOfEoi === undefined ? null : Number(values.numberOfEoi),
      numberOfBidsSubmitted:
        values.numberOfBidsSubmitted === "" || values.numberOfBidsSubmitted === undefined
          ? null
          : Number(values.numberOfBidsSubmitted),
      submittedParty: values.submittedParty || null,
      awardedParty: values.awardedParty || null,
      proposedPrice:
        values.proposedPrice === "" || values.proposedPrice === undefined
          ? null
          : Number(values.proposedPrice),
      proposedDuration: values.proposedDuration || null,
      detailsOfTheProject: values.detailsOfTheProject || null,
      bidCommitteeReference: values.bidCommitteeReference || null,
      agendaReference: values.agendaReference || null,
      budgetAllocations,
      fileName,
      base64File,
    };

    create.mutate(payload, {
      onSuccess: (data) => navigate(`/project-summary-sheets/${data.id}`),
    });
  };

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <div>
          <Title order={2}>New project summary sheet</Title>
          <Text c="dimmed" size="sm">
            PR-03 — complete fields; add one row per budget year and amount.
          </Text>
        </div>
        <Card shadow="sm" p="lg">
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <Group grow>
                <TextInput
                  label="Summary sheet number"
                  required
                  readOnly
                  {...form.getInputProps("summarySheetNumber")}
                />
                <DatePickerInput label="Sheet date" required {...form.getInputProps("sheetDate")} />
              </Group>
              <Group grow>
                <TextInput label="Project number" {...form.getInputProps("projectNumber")} />
                <div className="thaana-input">
                  <TextInput
                    label="Project name"
                    required
                    dir="rtl"
                    styles={thaanaFieldInputStyles()}
                    name="projectName"
                    value={form.values.projectName}
                    onChange={(e) => form.setFieldValue("projectName", e.currentTarget.value)}
                    error={form.errors.projectName}
                  />
                </div>
              </Group>
              <div className="thaana-input">
                <TextInput
                  label="Funding agency"
                  dir="rtl"
                  styles={thaanaFieldInputStyles()}
                  name="fundingAgency"
                  value={form.values.fundingAgency}
                  onChange={(e) => form.setFieldValue("fundingAgency", e.currentTarget.value)}
                />
              </div>
              <div className="thaana-input pss-auto-resize-textarea">
                <Textarea
                  label="Scope of work (detail)"
                  minRows={6}
                  dir="rtl"
                  styles={{
                    input: {
                      ...thaanaFieldInputStyles().input,
                      ...autoResizeTextareaStyles,
                    },
                  }}
                  name="scopeOfWorkDetail"
                  value={form.values.scopeOfWorkDetail || ""}
                  onChange={(e) => form.setFieldValue("scopeOfWorkDetail", e.currentTarget.value)}
                  onInput={(e) => resizeTextareaToContent(e.currentTarget)}
                />
              </div>
              <NumberInput label="Estimated cost (MVR)" min={0} decimalScale={2} thousandSeparator="," {...form.getInputProps("estimatedCost")} />
              <Divider label="Allocated budget by year" labelPosition="center" />
              {form.values.budgetAllocations.map((_, idx) => (
                <Group key={idx} align="flex-end" grow>
                  <NumberInput label="Year" placeholder="e.g. 2026" {...form.getInputProps(`budgetAllocations.${idx}.budgetYear`)} />
                  <NumberInput label="Amount (MVR)" min={0} decimalScale={2} thousandSeparator="," {...form.getInputProps(`budgetAllocations.${idx}.allocatedAmount`)} />
                  <ActionIcon color="red" variant="light" onClick={() => form.removeListItem("budgetAllocations", idx)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))}
              <Button variant="light" leftSection={<IconPlus size={16} />} type="button" onClick={addBudgetRow}>
                Add budget year
              </Button>
              <Divider label="Procurement history" labelPosition="center" />
              <Group grow>
                <NumberInput label="Times announced" min={0} {...form.getInputProps("numberOfTimesAnnounced")} />
                <DatePickerInput label="Date announced" clearable {...form.getInputProps("dateAnnounced")} />
              </Group>
              <Group grow>
                <NumberInput label="Number of EOI" min={0} {...form.getInputProps("numberOfEoi")} />
                <NumberInput label="Bids submitted" min={0} {...form.getInputProps("numberOfBidsSubmitted")} />
              </Group>
              <div className="thaana-input">
                <TextInput
                  label="Submitted party"
                  dir="rtl"
                  styles={thaanaFieldInputStyles()}
                  name="submittedParty"
                  value={form.values.submittedParty}
                  onChange={(e) => form.setFieldValue("submittedParty", e.currentTarget.value)}
                />
              </div>
              <Divider label="Recommendation of award" labelPosition="center" />
              <div className="thaana-input">
                <TextInput
                  label="Awarded party"
                  dir="rtl"
                  styles={thaanaFieldInputStyles()}
                  name="awardedParty"
                  value={form.values.awardedParty}
                  onChange={(e) => form.setFieldValue("awardedParty", e.currentTarget.value)}
                />
              </div>
              <NumberInput label="Proposed price (MVR)" min={0} decimalScale={2} thousandSeparator="," {...form.getInputProps("proposedPrice")} />
              <div className="thaana-input">
                <TextInput
                  label="Proposed duration"
                  dir="rtl"
                  styles={thaanaFieldInputStyles()}
                  name="proposedDuration"
                  value={form.values.proposedDuration}
                  onChange={(e) => form.setFieldValue("proposedDuration", e.currentTarget.value)}
                />
              </div>
              <Group justify="flex-end">
                <RegulatoryClausePicker
                  onInsert={(text) =>
                    form.setFieldValue(
                      "detailsOfTheProject",
                      `${form.values.detailsOfTheProject || ""}${text}`
                    )
                  }
                />
              </Group>
              <div className="thaana-input pss-auto-resize-textarea">
                <Textarea
                  label="Details of the project"
                  minRows={10}
                  dir="rtl"
                  styles={{
                    input: {
                      ...thaanaFieldInputStyles().input,
                      ...autoResizeTextareaStyles,
                    },
                  }}
                  name="detailsOfTheProject"
                  value={form.values.detailsOfTheProject || ""}
                  onChange={(e) => form.setFieldValue("detailsOfTheProject", e.currentTarget.value)}
                  onInput={(e) => resizeTextareaToContent(e.currentTarget)}
                />
              </div>
              <Group grow>
                <TextInput label="Bid committee ref." {...form.getInputProps("bidCommitteeReference")} />
                <TextInput label="Agenda ref." {...form.getInputProps("agendaReference")} />
              </Group>
              <FileInput label="Optional scan (PDF)" accept="application/pdf" value={file} onChange={setFile} clearable />
              <Group justify="space-between" mt="md">
                <Button variant="default" type="button" onClick={() => navigate("/project-summary-sheets")}>
                  Cancel
                </Button>
                <Button type="submit" loading={create.isPending}>
                  Create
                </Button>
              </Group>
            </Stack>
          </form>
        </Card>
      </Stack>
    </Container>
  );
}
